import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Download, FileText, TrendingUp, TrendingDown, Share2, Printer } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { chartColors } from "@/lib/theme/chartTheme";
import type { ReportConfig } from "./GenerateReportDialog";
import { format } from "date-fns";

interface ReportViewerProps {
  config: ReportConfig;
  onClose: () => void;
}

// Mock data - in production this would come from API
const assetClassData = [
  { name: "Public Equity", value: 12500000, percentage: 35, change: 8.5 },
  { name: "Fixed Income", value: 8900000, percentage: 25, change: 2.1 },
  { name: "Alternatives", value: 7100000, percentage: 20, change: 12.3 },
  { name: "Real Estate", value: 5400000, percentage: 15, change: 5.7 },
  { name: "Cash", value: 1800000, percentage: 5, change: 0.5 },
];

const geographyData = [
  { name: "North America", value: 18200000, percentage: 51 },
  { name: "Europe", value: 8900000, percentage: 25 },
  { name: "Asia Pacific", value: 5700000, percentage: 16 },
  { name: "Emerging Markets", value: 2900000, percentage: 8 },
];

const performanceData = [
  { month: "Jul", value: 32100000 },
  { month: "Aug", value: 33500000 },
  { month: "Sep", value: 33200000 },
  { month: "Oct", value: 34800000 },
  { month: "Nov", value: 35200000 },
  { month: "Dec", value: 35700000 },
];

const topHoldings = [
  { name: "Apple Inc.", ticker: "AAPL", value: 2800000, percentage: 7.8, change: 15.2 },
  { name: "Microsoft Corp.", ticker: "MSFT", value: 2500000, percentage: 7.0, change: 12.8 },
  { name: "Amazon.com Inc.", ticker: "AMZN", value: 1900000, percentage: 5.3, change: 8.4 },
  { name: "Vista Equity Partners Fund VI", ticker: "N/A", value: 1800000, percentage: 5.0, change: 18.5 },
  { name: "BlackRock World Bond Fund", ticker: "BWBFX", value: 1600000, percentage: 4.5, change: 3.2 },
];

export function ReportViewer({ config, onClose }: ReportViewerProps) {
  const totalValue = assetClassData.reduce((sum, item) => sum + item.value, 0);
  const totalChange = ((35700000 - 32100000) / 32100000) * 100;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  const handleExport = (_type: "pdf" | "excel") => {
    // TODO: implement actual export
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                <CardTitle className="text-2xl">
                  {config.reportType === "consolidated" ? "Consolidated Portfolio Report" : "Individual Asset Report"}
                </CardTitle>
              </div>
              <CardDescription className="text-base">
                {config.analysisType === "single" 
                  ? `As of ${config.startDate ? format(config.startDate, "MMMM dd, yyyy") : "N/A"}`
                  : `Period: ${config.startDate ? format(config.startDate, "MMM dd, yyyy") : "N/A"} - ${config.endDate ? format(config.endDate, "MMM dd, yyyy") : "N/A"}`
                }
              </CardDescription>
              {config.watermark && (
                <Badge variant="outline" className="mt-2">{config.watermark}</Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport("pdf")}>
                <Download className="h-4 w-4" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport("excel")}>
                <Download className="h-4 w-4" />
                Excel
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              <Button variant="outline" size="sm">
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Summary Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Portfolio Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
            <div className="flex items-center gap-1 text-sm mt-1">
              {totalChange >= 0 ? (
                <TrendingUp className="h-4 w-4 text-success" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className={totalChange >= 0 ? "text-success" : "text-destructive"}>
                {formatPercentage(totalChange)}
              </span>
              <span className="text-muted-foreground">vs. 6M ago</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Positions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">142</div>
            <p className="text-xs text-muted-foreground mt-1">Across 5 asset classes</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">YTD Return</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">+11.2%</div>
            <p className="text-xs text-muted-foreground mt-1">Outperforming benchmark</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Liquid Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">78%</div>
            <p className="text-xs text-muted-foreground mt-1">Can be liquidated in 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Asset Class Breakdown */}
      {config.breakdowns.includes("asset_class") && (
        <Card>
          <CardHeader>
            <CardTitle>Asset Class Allocation</CardTitle>
            <CardDescription>Current distribution across asset classes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={assetClassData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} (${percentage}%)`}
                      outerRadius={100}
                      fill={chartColors[0]}
                      dataKey="value"
                    >
                      {assetClassData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset Class</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assetClassData.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right">
                          <div>
                            <div className="font-medium">{formatCurrency(item.value)}</div>
                            <div className="text-xs text-muted-foreground">{item.percentage}%</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={item.change >= 0 ? "text-success" : "text-destructive"}>
                            {formatPercentage(item.change)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Performance Chart */}
      {config.analysisType === "comparative" && (
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Performance</CardTitle>
            <CardDescription>6-month portfolio value trend</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke={chartColors[0]} strokeWidth={2} name="Portfolio Value" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geography Breakdown */}
      {config.breakdowns.includes("geography") && (
        <Card>
          <CardHeader>
            <CardTitle>Geographic Distribution</CardTitle>
            <CardDescription>Asset allocation by region</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={geographyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="value" fill={chartColors[1]} name="Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Holdings */}
      {config.reportType === "individual" && (
        <Card>
          <CardHeader>
            <CardTitle>Top Holdings</CardTitle>
            <CardDescription>Largest positions by market value</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Position</TableHead>
                  <TableHead>Ticker</TableHead>
                  <TableHead className="text-right">Market Value</TableHead>
                  <TableHead className="text-right">% of Portfolio</TableHead>
                  <TableHead className="text-right">Gain/Loss</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topHoldings.map((holding) => (
                  <TableRow key={holding.name}>
                    <TableCell className="font-medium">{holding.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{holding.ticker}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(holding.value)}
                    </TableCell>
                    <TableCell className="text-right">
                      {holding.percentage.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={holding.change >= 0 ? "text-success" : "text-destructive"}>
                        {formatPercentage(holding.change)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Footer Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={() => handleExport("pdf")}>
          <Download className="h-4 w-4" />
          Export Report
        </Button>
      </div>
    </div>
  );
}
